/**
 * SEO measurement.
 *
 * "The counter is a bar, not a number. Green to 60, amber to 70, hard stop at
 * 70. Pixel width, not character count — Cyrillic is narrower than Latin, so a
 * character count lies." (§10.4/4, §3.2)
 *
 * Google truncates a title at roughly 580 CSS pixels of its serp font and a
 * description at roughly 990px across two lines. We measure the real string in
 * a canvas at those metrics, which is the only way "54/60" stops being a
 * comforting fiction on a Russian title.
 */

export const META_TITLE_MAX = 70;
export const META_TITLE_SOFT = 60;
export const META_DESC_MAX = 180;
export const META_DESC_SOFT = 160;

/** Google's SERP title is ~20px Arial; the description ~14px. */
const TITLE_FONT = '400 20px Arial, sans-serif';
const DESC_FONT = '400 14px Arial, sans-serif';
export const TITLE_PIXEL_LIMIT = 580;
export const DESC_PIXEL_LIMIT = 1840; // two lines at ~920px

let ctx: CanvasRenderingContext2D | null = null;

function measureContext(): CanvasRenderingContext2D | null {
  if (ctx) return ctx;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  ctx = canvas.getContext('2d');
  return ctx;
}

export function pixelWidth(text: string, kind: 'title' | 'desc'): number {
  const context = measureContext();
  if (!context) return text.length * (kind === 'title' ? 9 : 6.5);
  context.font = kind === 'title' ? TITLE_FONT : DESC_FONT;
  return context.measureText(text).width;
}

export type MetaVerdict = 'empty' | 'short' | 'good' | 'long' | 'over';

export interface MetaMeasurement {
  chars: number;
  charMax: number;
  pixels: number;
  pixelLimit: number;
  /** 0–1, for the bar. Clamped so an over-long value still renders a full bar. */
  ratio: number;
  verdict: MetaVerdict;
  /** What Google would actually display. */
  truncated: string;
}

export function measureMeta(value: string | null | undefined, kind: 'title' | 'desc'): MetaMeasurement {
  const text = (value ?? '').trim();
  const charMax = kind === 'title' ? META_TITLE_MAX : META_DESC_MAX;
  const soft = kind === 'title' ? META_TITLE_SOFT : META_DESC_SOFT;
  const pixelLimit = kind === 'title' ? TITLE_PIXEL_LIMIT : DESC_PIXEL_LIMIT;

  const pixels = text ? pixelWidth(text, kind) : 0;
  const ratio = Math.min(1, pixels / pixelLimit);

  let verdict: MetaVerdict;
  if (!text) verdict = 'empty';
  else if (text.length > charMax) verdict = 'over';
  else if (pixels > pixelLimit) verdict = 'long';
  else if (text.length < soft * 0.45) verdict = 'short';
  else verdict = 'good';

  return { chars: text.length, charMax, pixels, pixelLimit, ratio, verdict, truncated: truncateToPixels(text, kind) };
}

/** What the SERP shows: cut at the pixel limit, on a word boundary, with an ellipsis. */
export function truncateToPixels(text: string, kind: 'title' | 'desc'): string {
  const limit = kind === 'title' ? TITLE_PIXEL_LIMIT : DESC_PIXEL_LIMIT;
  if (!text || pixelWidth(text, kind) <= limit) return text;

  let low = 0;
  let high = text.length;
  while (low < high) {
    const mid = Math.floor((low + high + 1) / 2);
    if (pixelWidth(`${text.slice(0, mid)}…`, kind) <= limit) low = mid;
    else high = mid - 1;
  }
  const cut = text.slice(0, low);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > low * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * The URL as Google renders it in a result: host, then breadcrumb segments.
 * Showing the real thing is what makes "Sahifa manzili" self-explanatory.
 */
export function serpUrl(siteUrl: string, locale: string, segment: string, slug: string): string {
  const host = siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return [host, locale, segment, slug].filter(Boolean).join(' › ');
}

export function liveUrl(siteUrl: string, locale: string, segment: string, slug: string): string {
  return `${siteUrl.replace(/\/$/, '')}/${locale}/${segment}/${slug}`;
}

/**
 * Slugify in the browser, matching the server's transliteration exactly.
 * Two implementations of one rule is how "the slug the UI showed" and "the
 * slug that got saved" drift apart.
 */
const CYRILLIC: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'j', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh',
  щ: 'sch', ъ: '', ы: 'i', ь: '', э: 'e', ю: 'yu', я: 'ya',
  ў: 'o', қ: 'q', ғ: 'g', ҳ: 'h', ə: 'a',
};

const LATIN_EXTRAS: Record<string, string> = {
  'ʻ': '', 'ʼ': '', '‘': '', '’': '', "'": '', '`': '',
  ä: 'a', ö: 'o', ü: 'u', ß: 'ss', é: 'e', è: 'e', ê: 'e', à: 'a', â: 'a',
  ç: 'c', î: 'i', ï: 'i', ô: 'o', û: 'u', ñ: 'n',
};

export function slugify(input: string): string {
  const lower = input.toLowerCase().trim();
  let out = '';
  for (const char of lower) {
    if (char in CYRILLIC) out += CYRILLIC[char];
    else if (char in LATIN_EXTRAS) out += LATIN_EXTRAS[char];
    else if (/[a-z0-9]/.test(char)) out += char;
    else out += '-';
  }
  return out.replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 96).replace(/-$/, '');
}

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const isValidSlug = (value: string) => SLUG_PATTERN.test(value) && value.length <= 96;

/** Plain text from a TipTap document — for the SERP description fallback. */
export function docToText(doc: unknown, max = 220): string {
  if (!doc || typeof doc !== 'object') return '';
  const parts: string[] = [];
  const walk = (node: Record<string, unknown>): void => {
    if (typeof node.text === 'string') parts.push(node.text);
    const content = node.content;
    if (Array.isArray(content)) for (const child of content) walk(child as Record<string, unknown>);
  };
  walk(doc as Record<string, unknown>);
  const text = parts.join(' ').replace(/\s+/g, ' ').trim();
  if (!Number.isFinite(max) || text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
}
